import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { Button, useToast } from "../../ui";
import { dispatchClientRefresh } from "../../../hooks/useClientRefresh";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import s from "./clientApp.module.css";

export default function ClientPaySessionFeeButton({
  charge,
  patientId,
}) {
  const toast = useToast();
  const { athleteQueryParam } = useClientAthlete();
  const [searchParams, setSearchParams] = useSearchParams();
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const due = charge?.session_fee_status === "due";
  const attendanceId = charge?.id;
  const amount = Number(charge?.session_fee_amount);

  useEffect(() => {
    if (!due || !attendanceId || !(amount > 0)) {
      setAvailable(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    clinicAPI
      .getOnlinePaymentAvailable({ attendance_id: attendanceId })
      .then((res) => {
        if (!cancelled) setAvailable(Boolean(res.available));
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [due, attendanceId, amount]);

  const trySync = async ({ clearParams } = {}) => {
    const res = await clinicAPI.syncOnlineSessionFeePayment({
      attendance_id: attendanceId,
      patient_id: patientId || undefined,
    });
    if (res.status === "confirmed") {
      toast.success("Оплата прошла — занятие оплачено");
      if (clearParams) {
        const next = new URLSearchParams(searchParams);
        next.delete("payment");
        next.delete("attendance");
        setSearchParams(next, { replace: true });
      }
      dispatchClientRefresh();
      window.setTimeout(() => window.location.reload(), 400);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!due || !attendanceId) return undefined;
    const fromReturn =
      searchParams.get("payment") === "success" &&
      String(searchParams.get("attendance") || "") === String(attendanceId);
    let cancelled = false;
    if (!fromReturn) return undefined;
    setSyncing(true);
    trySync({ clearParams: true })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSyncing(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due, attendanceId]);

  if (!due || loading) return null;
  if (!(amount > 0)) {
    return (
      <p className={s.profMuted}>
        Сумма за занятие уточняется в клубе
      </p>
    );
  }
  if (!available) return null;

  const pay = async () => {
    setPaying(true);
    try {
      const synced = await trySync();
      if (synced) return;
      const data = await clinicAPI.initOnlineSessionFeePayment({
        attendance_id: attendanceId,
        patient_id: patientId || undefined,
        ...(athleteQueryParam || {}),
      });
      if (data.payment_url && String(data.payment_url).startsWith("http")) {
        window.location.assign(data.payment_url);
        return;
      }
      toast.error("Ссылка на оплату не получена");
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось начать оплату");
    } finally {
      setPaying(false);
    }
  };

  return (
    <Button
      type="button"
      disabled={paying || syncing}
      onClick={pay}
      className={s.clientPayBtn}
    >
      {paying || syncing
        ? "…"
        : `Оплатить занятие · ${Math.round(amount).toLocaleString("ru-RU")} ₽`}
    </Button>
  );
}
